import React, { useState, useEffect } from 'react';
import axios from 'axios';

function CaptionsModal({ isOpen, onClose, recording, apiUrl, secret, apiBaseUrl }) {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [kind, setKind] = useState('subtitles');
  const [lang, setLang] = useState('en');
  const [label, setLabel] = useState('English');
  const [vttContent, setVttContent] = useState('');
  const [fileName, setFileName] = useState('');

  useEffect(() => {
    if (isOpen && recording?.recordID) {
      fetchTracks();
      setError('');
      setSuccess('');
      setVttContent('');
      setFileName('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, recording]);

  const fetchTracks = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${apiBaseUrl}/api/recordings/tracks/get`, {
        apiUrl,
        secret,
        recordID: recording.recordID
      });
      setTracks(res.data.tracks || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load captions.');
      setTracks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      setVttContent(event.target.result || '');
    };
    reader.onerror = () => {
      setError('Failed to read subtitle file.');
    };
    reader.readAsText(file);
  };

  const handleUploadTrack = async (e) => {
    e.preventDefault();
    if (!vttContent.trim()) {
      setError('Please select a .vtt caption file or paste WebVTT content.');
      return;
    }
    setUploading(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(`${apiBaseUrl}/api/recordings/tracks/put`, {
        apiUrl,
        secret,
        recordID: recording.recordID,
        kind,
        lang,
        label,
        content: vttContent
      });

      setSuccess(`Captions for "${label}" (${lang}) uploaded successfully!`);
      setVttContent('');
      setFileName('');
      fetchTracks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload captions.');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Recording Captions & Subtitles</h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--slate-500)', marginTop: '2px' }}>
              {recording.name || recording.recordID}
            </p>
          </div>
          <button className="btn-icon" onClick={onClose} aria-label="Close modal">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div style={{ padding: '0.75rem', background: 'var(--danger-light)', color: 'var(--danger-text)', borderRadius: 'var(--radius-md)', marginBottom: '1rem', fontSize: '0.875rem' }}>
              {error}
            </div>
          )}

          {success && (
            <div style={{ padding: '0.75rem', background: 'var(--success-light)', color: 'var(--success-text)', borderRadius: 'var(--radius-md)', marginBottom: '1rem', fontSize: '0.875rem' }}>
              {success}
            </div>
          )}

          <h4 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--slate-800)', marginBottom: '0.6rem' }}>
            Existing Caption Tracks
          </h4>

          {loading ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--slate-500)', fontSize: '0.875rem' }}>
              Loading captions...
            </div>
          ) : tracks.length === 0 ? (
            <div style={{ padding: '0.85rem', background: 'var(--slate-100)', borderRadius: 'var(--radius-md)', color: 'var(--slate-600)', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
              No captions or subtitles currently uploaded for this recording.
            </div>
          ) : (
            <div style={{ marginBottom: '1.25rem', border: '1px solid var(--slate-200)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead style={{ background: 'var(--slate-100)' }}>
                  <tr>
                    <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--slate-600)' }}>Language</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--slate-600)' }}>Label</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--slate-600)' }}>Kind</th>
                    <th style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--slate-600)' }}>Link</th>
                  </tr>
                </thead>
                <tbody>
                  {tracks.map((t, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--slate-200)' }}>
                      <td style={{ padding: '8px 10px', fontWeight: 600 }}>{t.lang || 'N/A'}</td>
                      <td style={{ padding: '8px 10px' }}>{t.label || 'N/A'}</td>
                      <td style={{ padding: '8px 10px', textTransform: 'capitalize' }}>{t.kind || 'subtitles'}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                        {t.href ? (
                          <a href={t.href} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>
                            View VTT
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <hr style={{ border: 'none', borderTop: '1px solid var(--slate-200)', margin: '1.25rem 0' }} />

          <h4 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--slate-800)', marginBottom: '0.8rem' }}>
            Upload New Subtitles / Captions (.vtt)
          </h4>

          <form onSubmit={handleUploadTrack}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.85rem' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.8rem' }}>Lang Code</label>
                <input
                  type="text"
                  className="form-control"
                  style={{ padding: '0.45rem 0.65rem', fontSize: '0.85rem' }}
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                  placeholder="en, fr, es..."
                  required
                />
              </div>

              <div>
                <label className="form-label" style={{ fontSize: '0.8rem' }}>Display Label</label>
                <input
                  type="text"
                  className="form-control"
                  style={{ padding: '0.45rem 0.65rem', fontSize: '0.85rem' }}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. English"
                  required
                />
              </div>

              <div>
                <label className="form-label" style={{ fontSize: '0.8rem' }}>Kind</label>
                <select
                  className="form-control"
                  style={{ padding: '0.45rem 0.65rem', fontSize: '0.85rem' }}
                  value={kind}
                  onChange={(e) => setKind(e.target.value)}
                >
                  <option value="subtitles">Subtitles</option>
                  <option value="captions">Captions</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.8rem' }}>Choose WebVTT File (.vtt)</label>
              <input
                type="file"
                accept=".vtt,text/vtt"
                onChange={handleFileUpload}
                style={{ fontSize: '0.85rem', width: '100%' }}
              />
              {fileName && (
                <div style={{ fontSize: '0.75rem', color: 'var(--slate-500)', marginTop: '4px' }}>
                  Loaded: <strong>{fileName}</strong> ({vttContent.length} characters)
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.8rem' }}>Or Paste WebVTT Content</label>
              <textarea
                className="form-control"
                rows={3}
                style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                placeholder={`WEBVTT\n\n1\n00:00:01.000 --> 00:00:05.000\nHello and welcome to the session.`}
                value={vttContent}
                onChange={(e) => setVttContent(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={uploading || !vttContent.trim()}
              >
                {uploading ? 'Uploading...' : 'Upload Text Track'}
              </button>
            </div>
          </form>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default CaptionsModal;
